import { useMemo } from 'react';

import {
  FetchSelectedProductGqlQuery,
  SelectedProductUUID
} from '../../selectedProductsTypes';

import { FetchSelectedProductCacheKey } from '../../selectedProductsTypes';

import { useShowQuery } from '../../../common/reactQuery/hooks/useShowQuery';

interface SelectedProductResponse<ItemType> {
  selectedProduct: ItemType | null;
}

interface SelectedProductOptions<ItemType> {
  selectedProducts?: ItemType[];
  cacheKey: FetchSelectedProductCacheKey;
  query: FetchSelectedProductGqlQuery;
  uuid: SelectedProductUUID;
  options?: {
    cacheTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
  };
}

interface UseSelectedProductItem {
  uuid: SelectedProductUUID;
}

const itemKey = 'selectedProduct';

function useSelectedProduct<ItemType extends UseSelectedProductItem>({
  selectedProducts,
  cacheKey,
  query,
  uuid,
  options = {}
}: SelectedProductOptions<ItemType>) {
  const placeholderData = useMemo<SelectedProductResponse<ItemType>>(() => {
    const selectedProduct = selectedProducts?.find(
      (selectedProduct) => selectedProduct.uuid === uuid
    );

    return selectedProduct ? { selectedProduct } : null;
  }, [selectedProducts, uuid]);

  const {
    item,
    itemError,
    itemErrorData,
    itemFetched,
    itemFetching,
    itemLoading,
    itemIsPlaceholderData,
    refetchItem,
    updateItemCache
  } = useShowQuery<SelectedProductResponse<ItemType>, ItemType>({
    cacheKey,
    itemKey,
    options,
    placeholderData,
    query,
    uuid
  });

  return {
    selectedProduct: item,
    selectedProductError: itemError,
    selectedProductErrorData: itemErrorData,
    selectedProductFetched: itemFetched,
    selectedProductFetching: itemFetching,
    selectedProductIsPlaceholderData: itemIsPlaceholderData,
    selectedProductLoading: itemLoading,
    selectedProductUpdateCache: updateItemCache,
    refetchSelectedProduct: refetchItem
  };
}

export default useSelectedProduct;
